'use client'

import { Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import StepUpVerificationFields from '@/components/dialog/settings/step-up-verification-fields'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { useAuth, usePasskey } from '@/features/auth/hooks'
import { getDeviceNameFromUserAgent } from '@/features/auth/hooks/use-passkey'
import { classifyStepUpError, pickStepUpVerification } from '@/lib/step-up'
import { toast } from '@/lib/toast'
import { useGet2FABackupCodes } from '@/service/user-profile.hook'
import { SensitiveAction } from '@/types/generated/common/auth'

type AddPasskeyFormData = {
  currentPassword?: string
  totpCode?: string
  backupCode?: string
  emailCode?: string
}

// A passkey signs in with neither the password nor TOTP, so adding one re-proves the
// account first (SENSITIVE_ACTION_ADD_PASSKEY), the same way removing one does.
export default function AddPasskeyDialog({
  refetchPasskeyList,
}: {
  refetchPasskeyList: () => void
}) {
  const t = useTranslations('settings')
  const { user, hasPassword } = useAuth()
  const { registerPasskey } = usePasskey()
  const email = user?.email
  const [isOpen, setIsOpen] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)
  const [factorOverride, setFactorOverride] = useState<'totp' | 'email' | null>(null)

  // Cached React Query — the parent already subscribes to this same key.
  const { data: backupCodes } = useGet2FABackupCodes({}, { treatMissingAsDisabled: true })
  const is2faEnabled = (backupCodes?.backupCodes.length ?? 0) > 0
  const totpFactor = factorOverride ? factorOverride === 'totp' : is2faEnabled

  const form = useForm<AddPasskeyFormData>()

  const close = () => {
    form.reset()
    setFactorOverride(null)
    setIsOpen(false)
  }

  const onSubmit = async (data: AddPasskeyFormData) => {
    // registerPasskey() opens the native WebAuthn prompt — a double submit before this
    // state flips would fire it twice concurrently (overlapping prompts, or an
    // InvalidStateError race).
    if (!email || isRegistering) return
    setIsRegistering(true)
    form.clearErrors()
    try {
      const response = await registerPasskey({
        email,
        deviceName: getDeviceNameFromUserAgent(),
        stepUp: {
          currentPassword: data.currentPassword ?? '',
          ...pickStepUpVerification(data),
        },
      })
      if (response.success) {
        toast.success(t('passkeyRegisteredSuccess'))
        refetchPasskeyList()
        close()
      } else {
        toast.error(response.errorMessage || t('passkeyRegistrationFailed'))
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === 'NotAllowedError') return // user cancelled
      if (e instanceof DOMException && e.name === 'InvalidStateError') {
        toast.error(t('authenticatorHasPasskey'))
        return
      }
      const message = (e as { message?: string })?.message
      // Never retried here: every failed attempt counts toward the step-up throttle.
      const failure = classifyStepUpError(message)
      switch (failure.kind) {
        case 'password':
          form.setError('currentPassword', { message: message || t('passkeyRegistrationFailed') })
          break
        case 'totp-required':
          setFactorOverride('totp')
          break
        case 'email-code-required':
          setFactorOverride('email')
          break
        case 'totp-invalid':
          form.setError(data.backupCode && !data.totpCode ? 'backupCode' : 'totpCode', {
            message: message || t('passkeyRegistrationFailed'),
          })
          break
        case 'email-code-invalid':
          form.setError('emailCode', { message: message || t('passkeyRegistrationFailed') })
          break
      }
      toast.error(message || t('passkeyRegistrationFailed'))
    } finally {
      setIsRegistering(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (open) setIsOpen(true)
        else close()
      }}
    >
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" disabled={!email}>
          {t('addPasskey')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle className="flex flex-col gap-2">
          {t('addPasskey')}
          <p className="text-sm font-normal tracking-tight text-white/60">
            {t('addPasskeyDescription')}
          </p>
        </DialogTitle>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 flex w-full flex-col gap-4">
          <StepUpVerificationFields
            form={form}
            hasPassword={hasPassword}
            totpEnabled={totpFactor}
            action={SensitiveAction.SENSITIVE_ACTION_ADD_PASSKEY}
          />
          {/* The code is not required client-side: the server says which factor it wants
              (TOTP_REQUIRED / EMAIL_CODE_REQUIRED), and a backend predating the step-up
              ignores these fields. */}
          <Button
            type="submit"
            className="w-full"
            disabled={isRegistering || (hasPassword && !form.watch('currentPassword'))}
          >
            {isRegistering && <Loader2Icon className="size-3.5 animate-spin" />}
            {isRegistering ? t('addingPasskey') : t('addPasskey')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
